import axios from 'axios';
import aiImagesApi from 'common/src/modules/aiImages/aiImages.api';
import {AssetType} from 'common/src/modules/Assets/Assets.enums';
import findAssetById from 'common/src/modules/Assets/utils/findAssetById';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';
import getAssetFileUrl from 'common/src/modules/Assets/utils/getAssetFileUrl';
import {CreativeTemplateType} from 'common/src/modules/creativeTemplates/creativeTemplates.types';

const inputSchema = z.object({
  assetId: z.number().describe('The numeric ID of the image asset to extract text layers from'),
  mergeLevel: z.number().nullable().optional().describe('Not in use at the moment (0.0 to 1.0)'),
});

function extractTextLayersFromImageTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.ExtractTextLayersFromImage,
    displayName: 'Extracting text layers from image',
    description: 'Extracts text layers and their properties from an image returning a JSON object',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const state = store.getState();

      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return toolResult('ERROR: No active composition found');
      }

      try {
        // Find the asset by ID from template assets tree
        const assetTree = state.creativeTemplatePage.templateAssets.assetTree;
        const asset = findAssetById(assetTree, input.assetId);

        if (!asset) {
          return toolResult(`ERROR: Asset with ID "${input.assetId}" not found in template assets`);
        }

        // Check if the asset is an animated image (not supported)
        if (asset.assetType === AssetType.AnimatedImage) {
          return toolResult(
            `ERROR: Cannot extract text layers from animated image "${asset.name}". Only static images are supported.`,
          );
        }

        // Check if the asset is an image
        if (asset.assetType !== AssetType.Image) {
          return toolResult(
            `ERROR: Asset "${asset.name}" is not an image (type: ${asset.assetType})`,
          );
        }

        // Get the image URL (prefer compressed image, fallback to original)
        const imageUrl =
          getAssetFileUrl(asset, CreativeTemplateType.ImageAds) || asset.originalfile.fileUrl;

        if (!imageUrl) {
          return toolResult(`ERROR: No image URL found for asset "${asset.name}"`);
        }

        // Call the AI Images API to extract text layers from URL
        const result = await aiImagesApi.extractTextLayersFromImageUrl({
          imageUrl,
          mergeLevel: input.mergeLevel,
          vendorService: 15, // GoogleDocumentAIExtractTextLayers - you may need to adjust this number
          cancelToken: axios.CancelToken.source().token,
        });

        return toolResult(JSON.stringify(result, null, 2));
      } catch (error) {
        return toolResult(
          `ERROR: ${error instanceof Error ? error.message : 'Failed to extract text layers from image'}`,
        );
      }
    },
  });
}

export default extractTextLayersFromImageTool;
